/**
 * RichEditor configuration + sanitiser — kept React-free so it can be unit
 * tested headlessly (see tests/unit/richEditor.test.ts) and imported without
 * pulling in @tiptap/react / React.
 *
 * `RichEditor.tsx` is the thin React shell around this.
 */
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';

/** Tags the adviser narrative fields are allowed to persist. */
const ALLOWED_TAGS = new Set(['B', 'STRONG', 'I', 'EM', 'P', 'BR', 'H2', 'H3', 'UL', 'OL', 'LI', 'A']);

/**
 * Defensively strip HTML down to the allowed tag set. Disallowed elements are
 * unwrapped (children kept), every attribute is dropped except a protocol-checked
 * `href` on anchors, and comment / processing nodes are removed. Used both on the
 * way into the editor (legacy records pre-date this editor) and on the way out.
 *
 * No-ops when there is no DOM (e.g. SSR) — callers get the input back untouched.
 */
export function sanitiseRichText(html: string): string {
  if (typeof document === 'undefined') return html;
  const tmpl = document.createElement('template');
  tmpl.innerHTML = html;
  const walk = (node: Node) => {
    // Snapshot the child list — it is mutated below (unwrap / remove) and a
    // live NodeList would skip nodes shifted by the mutation.
    for (const child of [...node.childNodes]) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const el = child as HTMLElement;
        // Always sanitise the subtree first, so children promoted by an unwrap
        // are already clean — a disallowed tag nested in a disallowed wrapper
        // (e.g. <script> inside <div>) must not survive.
        walk(el);
        if (!ALLOWED_TAGS.has(el.tagName)) {
          while (el.firstChild) el.parentNode!.insertBefore(el.firstChild, el);
          el.parentNode?.removeChild(el);
          continue;
        }
        for (const attr of [...el.attributes]) {
          if (el.tagName === 'A' && attr.name === 'href') {
            if (!/^(https?:|mailto:)/i.test(attr.value)) el.removeAttribute('href');
          } else {
            el.removeAttribute(attr.name);
          }
        }
      } else if (child.nodeType !== Node.TEXT_NODE) {
        child.parentNode?.removeChild(child);
      }
    }
  };
  walk(tmpl.content);
  return tmpl.innerHTML;
}

/**
 * The TipTap extension stack the editor runs: StarterKit (bold/italic, lists,
 * undo/redo, history) with headings constrained to H2/H3, protocol-checked Link,
 * and a Placeholder. Exported so the component and its tests share one config.
 */
export function buildRichEditorExtensions(placeholder?: string) {
  return [
    StarterKit.configure({
      heading: { levels: [2, 3] },
    }),
    Link.configure({
      openOnClick: false,
      autolink: true,
      protocols: ['http', 'https', 'mailto'],
      HTMLAttributes: { rel: 'noopener noreferrer', class: 'underline text-rust' },
    }),
    Placeholder.configure({
      placeholder: placeholder ?? 'Start typing…',
      emptyEditorClass: 'is-editor-empty',
    }),
  ];
}
